import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Calendar, Truck, Package, Bell, MessageCircle, Phone,
  LogOut, Plus, CheckCircle, Clock, Loader2, User,
  Home, ChevronRight, Shirt, AlertCircle, LayoutDashboard
} from 'lucide-react';
import CustomerBottomNav from '@/components/CustomerBottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Sidebar nav items ────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/customer/dashboard', label: 'Dashboard',  icon: LayoutDashboard },
  { path: '/customer/orders',    label: 'My Orders',  icon: Package },
  { path: '/customer/chat',      label: 'Support',    icon: MessageCircle },
  { path: '/customer/profile',   label: 'Profile',    icon: User },
];

// ── Status helpers ───────────────────────────────────────────────────────────
const STATUS_STEPS = [
  { key: 'pending',   label: 'Pending',   icon: Clock },
  { key: 'pickup',    label: 'Pickup',    icon: Truck },
  { key: 'processed', label: 'Washing',   icon: Shirt },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const STATUS_ORDER = ['pending', 'pickup', 'processed', 'delivered'];

const STATUS_BADGE = {
  pending:   'bg-amber-50  text-amber-700  border-amber-200',
  pickup:    'bg-blue-50   text-blue-700   border-blue-200',
  processed: 'bg-orange-50 text-orange-700 border-orange-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  delivery_overdue: 'bg-red-50 text-red-700 border-red-200',
};

const CustomerDashboard = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const [customer, setCustomer]   = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [unreadMsg,   setUnreadMsg]   = useState(0);

  const prevNotif = useRef(0);
  const prevMsg   = useRef(0);
  const audioRef  = useRef(null);
  const firstLoad = useRef(true);
  const token     = localStorage.getItem('customerToken');

  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/notification.mp3');
        audioRef.current.volume = 0.6;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (_) {}
  }, []);

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/customer/login'); return; }
    try {
      const [dashRes, notifRes, chatRes] = await Promise.all([
        axios.get(`${API}/customer/dashboard`,                    { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customer/notifications/unread-count`,   { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customer/chat/unread-count`,            { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setDashboard(dashRes.data);
      setCustomer({ name: dashRes.data.customer_name, pg_name: dashRes.data.pg_name });

      const nc = notifRes.data.count;
      const mc = chatRes.data.count;
      if (!firstLoad.current) {
        if (nc > prevNotif.current) { playSound(); toast.info('You have a new notification', { icon: '🔔' }); }
        else if (mc > prevMsg.current) { playSound(); toast.info('New message from JustFold', { icon: '💬' }); }
      }
      firstLoad.current = false;
      prevNotif.current = nc;
      prevMsg.current   = mc;
      setUnreadNotif(nc);
      setUnreadMsg(mc);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Session expired');
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
        navigate('/customer/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, playSound]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    navigate('/customer/login');
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 animate-pulse">
            <img src="/justfold-logo-transparent.png" alt="" className="w-7 h-7" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  const activeOrder   = dashboard?.active_order;
  const recentOrders  = dashboard?.recent_orders || [];
  const nextPickup    = dashboard?.next_pickup;
  const activeIdx     = activeOrder ? STATUS_ORDER.indexOf(activeOrder.status) : -1;

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const Sidebar = () => (
    <aside className="hidden md:flex flex-col w-64 flex-shrink-0 bg-white border-r border-slate-200 h-screen sticky top-0">
      {/* Logo */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
        <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
          <img src="/logo.png" alt="JustFold" className="h-9 w-auto object-contain" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <button key={path} onClick={() => navigate(path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                active
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}>
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left">{label}</span>
              {label === 'Support' && unreadMsg > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadMsg}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Need Help + Logout */}
      <div className="px-4 py-4 border-t border-slate-100 space-y-3">
        <div className="bg-slate-50 rounded-2xl p-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Need Help?</p>
          <button onClick={() => navigate('/customer/chat')}
            className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            Contact Support
          </button>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 border border-red-100 transition-all">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Sidebar />

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between flex-shrink-0 z-10">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="JustFold" className="h-8 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/customer/notifications')}
              className="relative w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
              <Bell className="w-5 h-5" />
              {unreadNotif > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadNotif > 9 ? '9+' : unreadNotif}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* ── Welcome Banner ── */}
            <header className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] px-8 py-8 text-white overflow-hidden shadow-xl shadow-blue-200">
              {/* Decorative circles */}
              <div className="absolute -right-8 -top-8 w-44 h-44 bg-white/10 rounded-full" />
              <div className="absolute right-16 bottom-0 w-24 h-24 bg-white/5 rounded-full" />
              {/* Dot grid */}
              <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <div className="relative z-10">
                <p className="text-blue-200 text-sm font-medium mb-1">{greeting()}</p>
                <h1 className="text-3xl md:text-4xl font-black flex items-center gap-3 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {customer?.name?.split(' ')[0]}! <span className="text-3xl">👋</span>
                </h1>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
                  <p className="text-blue-100 font-medium text-sm">{customer?.pg_name}</p>
                </div>

                {/* Desktop notification bell */}
                <button onClick={() => navigate('/customer/notifications')}
                  className="hidden md:flex absolute top-6 right-6 items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/20 rounded-xl px-3 py-2 text-white text-xs font-semibold transition-all">
                  <Bell className="w-4 h-4" />
                  {unreadNotif > 0
                    ? <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{unreadNotif}</span>
                    : 'Notifications'
                  }
                </button>
              </div>
            </header>

            {/* ── Next Pickup ── */}
            <section className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-5 flex items-center gap-5 shadow-sm">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5">Next Pickup</p>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {nextPickup
                    ? format(new Date(nextPickup), 'EEEE, dd MMM yyyy')
                    : 'Not scheduled yet'}
                </h2>
              </div>
              {nextPickup && (
                <div className="ml-auto hidden sm:flex flex-col items-end">
                  <span className="text-xs text-slate-400">Pickup day</span>
                  <span className="text-sm font-bold text-blue-600">
                    {format(new Date(nextPickup), 'EEEE')}
                  </span>
                </div>
              )}
            </section>

            {/* ── Active Order / Empty State ── */}
            {activeOrder ? (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Current Order</h3>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_BADGE[activeOrder.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {activeOrder.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {/* Order details row */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shirt className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">{activeOrder.total_clothes} items</p>
                    <p className="text-xs text-slate-500">{activeOrder.clothes_type || 'Mixed clothing'}</p>
                  </div>
                  {activeOrder.expected_delivery_date && (
                    <div className="ml-auto text-right">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest">Expected</p>
                      <p className="text-xs font-bold text-slate-700">
                        {format(new Date(activeOrder.expected_delivery_date), 'dd MMM')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Progress tracker */}
                <div className="relative">
                  <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-slate-100" />
                  <div
                    className="absolute top-5 left-[10%] h-0.5 bg-blue-600 transition-all duration-700"
                    style={{ width: activeIdx <= 0 ? '0%' : `${(activeIdx / (STATUS_STEPS.length - 1)) * 80}%` }}
                  />
                  <div className="relative flex justify-between">
                    {STATUS_STEPS.map(({ key, label, icon: Icon }, i) => {
                      const done    = i < activeIdx;
                      const current = i === activeIdx;
                      return (
                        <div key={key} className="flex flex-col items-center gap-1.5 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                            done    ? 'bg-blue-600 text-white shadow-md shadow-blue-200' :
                            current ? 'bg-blue-600 text-white shadow-lg shadow-blue-300 scale-110' :
                                      'bg-slate-100 text-slate-400'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`text-[10px] font-bold ${current || done ? 'text-blue-600' : 'text-slate-400'}`}>
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {activeOrder.status === 'delivery_overdue' && (
                  <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600 font-medium">Delivery is overdue. Contact support for an update.</p>
                  </div>
                )}
              </section>
            ) : (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] py-16 md:py-20 px-8 flex flex-col items-center justify-center text-center shadow-sm space-y-5">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-300">
                  <Package className="w-12 h-12" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-500 mb-1">No active orders</p>
                  <p className="text-sm text-slate-400">Place an order and we'll pick it up on your next scheduled day</p>
                </div>
                <button
                  onClick={() => navigate('/customer/new-order')}
                  data-testid="place-order-btn"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-2xl shadow-md shadow-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-200"
                >
                  <Plus className="w-5 h-5" />
                  Place New Order
                </button>
              </section>
            )}

            {/* ── Place New Order CTA (when active order exists) ── */}
            {activeOrder && (
              <button
                onClick={() => navigate('/customer/new-order')}
                className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-500 font-semibold py-3.5 rounded-2xl text-sm transition-all cursor-not-allowed"
                disabled
              >
                <Clock className="w-4 h-4" />
                Order in progress — new order available after delivery
              </button>
            )}

            {/* ── Recent Orders ── */}
            {recentOrders.length > 0 && (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-black text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Orders</h3>
                  <button onClick={() => navigate('/customer/orders')}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-0.5 transition-colors">
                    View all <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="divide-y divide-slate-50">
                  {recentOrders.slice(0, 3).map((order) => (
                    <div key={order.id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{order.total_clothes} items</p>
                          <p className="text-xs text-slate-400">{format(new Date(order.order_date), 'dd MMM yyyy')}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${STATUS_BADGE[order.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Support Grid ── */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/customer/chat')}
                className="relative bg-white border border-slate-200/80 rounded-2xl p-5 flex flex-col items-center justify-center gap-2.5 hover:bg-slate-50 hover:border-blue-200 hover:shadow-md transition-all group shadow-sm col-span-2"
              >
                {unreadMsg > 0 && (
                  <span className="absolute top-3 right-3 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadMsg}</span>
                )}
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-slate-700">Chat Support</span>
              </button>
            </div>

            {/* ── Call Contacts ── */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-5 py-4 shadow-sm space-y-3">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Us</p>
              <a href="tel:+918384082073"
                className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-green-50 border border-transparent hover:border-green-200 transition-all group">
                <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center group-hover:bg-green-100 flex-shrink-0">
                  <Phone className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-700">Placing Order</p>
                  <p className="text-[11px] text-slate-400">8384082073</p>
                </div>
              </a>
              <a href="tel:+919311108140"
                className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all group">
                <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 flex-shrink-0">
                  <Phone className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-700">For Enquiry</p>
                  <p className="text-[11px] text-slate-400">9311108140</p>
                </div>
              </a>
              <a href="tel:+919821381888"
                className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-all group">
                <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center group-hover:bg-orange-100 flex-shrink-0">
                  <Phone className="w-4 h-4 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-700">Customer Complaints</p>
                  <p className="text-[11px] text-slate-400">9821381888</p>
                </div>
              </a>
            </div>

          </div>
        </main>
      </div>

      <CustomerBottomNav />
    </div>
  );
};

export default CustomerDashboard;
